import React from "react";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import ErrorMessage from "@/ui/atoms/ErrorMessage";
import InfoMessage from "@/ui/atoms/InfoMessage";

import { isNotNullable } from "@/utils/guards";
import Dropdown from "@/ui/molecules/Dropdown";
import ChartOfAccountsCard from "@/ui/molecules/ChartOfAccountsCard/ChartOfAccountsCard";

export type ChartsOfAccountsListProps = BoxProps & {
    coasRequestStatus: "idle" | "pending" | "rejected" | "resolved";
    coas:
        | {
              coaId: number;
              name: string;
              deals?: number;
              lastEdited?: string;
              lastEditedBy?: string;
              type?: string;
              ownerCompanyName?: string;
              ownerCompanyId?: number;
              isDraft: boolean;
          }[]
        | null;
    onClickOnDraftCoa?: (coaId: number) => void;
    onClickOnCoa?: (coaId: number) => void;
};

const coaHasCompanyIdAndNameTypeGuard = (coa: {
    ownerCompanyName?: string;
    ownerCompanyId?: number;
}): coa is { ownerCompanyName: string; ownerCompanyId: number } => {
    return (
        isNotNullable(coa.ownerCompanyName) && isNotNullable(coa.ownerCompanyId)
    );
};

const ALL_COMPANIES_OPTION = {
    label: "All companies",
    value: -1000,
};

const testId = "ChartsOfAccountsList";
const ChartsOfAccountsList = (props: ChartsOfAccountsListProps) => {
    const {
        coasRequestStatus,
        coas,
        onClickOnDraftCoa,
        onClickOnCoa,
        ...otherProps
    } = props;

    const [selectedCompanyId, setSelectedCompanyId] = React.useState<
        number | null
    >(ALL_COMPANIES_OPTION.value);

    const uniqueCompanyOptions:
        | {
              label: string;
              value: number;
          }[]
        | null = React.useMemo(() => {
        if (!coas) {
            return null;
        }
        const coasWithCompanyIdAndName = coas.filter(
            coaHasCompanyIdAndNameTypeGuard,
        ) as {
            ownerCompanyName: string;
            ownerCompanyId: number;
        }[];
        const coasOptions = coasWithCompanyIdAndName.map(coa => ({
            label: coa.ownerCompanyName,
            value: coa.ownerCompanyId,
        }));
        const uniqueCoasOptions = coasOptions.filter(
            (option, index, allOptions) => {
                return (
                    index ===
                    allOptions.findIndex(o => o.value === option.value)
                );
            },
        );
        return uniqueCoasOptions;
    }, [coas]);

    const dropdownOptions = React.useMemo(() => {
        if (!uniqueCompanyOptions) {
            return null;
        }
        return [ALL_COMPANIES_OPTION, ...uniqueCompanyOptions];
    }, [uniqueCompanyOptions]);

    const filteredCoas = React.useMemo(() => {
        if (!coas) {
            return null;
        }
        if (selectedCompanyId === ALL_COMPANIES_OPTION.value) {
            return coas;
        }
        return coas.filter(coa => coa.ownerCompanyId === selectedCompanyId);
    }, [coas, selectedCompanyId]);

    return (
        <Box
            {...otherProps}
            data-testid={testId}
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "16px",
                maxWidth: "1200px",
                margin: "80px auto",
                width: "100%",
            }}
        >
            {coasRequestStatus === "pending" && (
                <>
                    {[1, 2, 3].map(item => (
                        <ChartOfAccountsCard
                            key={item}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                width: "100%",
                            }}
                        />
                    ))}
                </>
            )}

            {coasRequestStatus === "rejected" && (
                <ErrorMessage
                    sx={{
                        width: "fit-content",
                    }}
                >
                    Unable to load charts of accounts. Please try again later.
                </ErrorMessage>
            )}

            {coasRequestStatus === "resolved" &&
                Array.isArray(coas) &&
                coas.length === 0 && (
                    <InfoMessage
                        sx={{
                            width: "fit-content",
                        }}
                    >
                        No charts of accounts available.
                    </InfoMessage>
                )}

            {coasRequestStatus === "resolved" &&
                Array.isArray(filteredCoas) &&
                filteredCoas.length > 0 && (
                    <>
                        {dropdownOptions && (
                            <Dropdown
                                label="Company"
                                options={dropdownOptions}
                                value={selectedCompanyId}
                                onChange={setSelectedCompanyId}
                            />
                        )}
                        {filteredCoas.map(coa => (
                            <ChartOfAccountsCard
                                key={coa.coaId}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    width: "100%",
                                    cursor: "pointer",
                                }}
                                isProdaStandard={coa.coaId === 1}
                                isDraft={coa.isDraft}
                                onClick={() => {
                                    if (coa.isDraft) {
                                        onClickOnDraftCoa?.(coa.coaId);
                                    } else {
                                        onClickOnCoa?.(coa.coaId);
                                    }
                                }}
                                coaDefinition={coa}
                            />
                        ))}
                    </>
                )}
        </Box>
    );
};

export default ChartsOfAccountsList;
